// Haut du tiroir panier (snippets/snk-cart-top.liquid).
// - snk-cart-cutoff : bandeau d'expédition à l'heure de Paris. Jour ouvré avant l'heure limite : « Commande dans
//   3 h 31 → expédiée aujourd'hui » (réglages de la section cart-drawer, à la minute) ; dernière heure : compte à
//   rebours min : s jusqu'à l'heure limite, qui ne se relance jamais ; après, le week-end et les jours fériés :
//   « Expédiée demain » ou le prochain jour ouvré. Le minuteur tourne toujours (panier ouvert depuis la veille).
// - snk-cart-tiers : jauge des paliers ; dans une autre devise que l'euro, recalcule le montant restant et le
//   remplissage au taux de Shopify (window.snkMoney, assets/snk-money.js).

// Instant « now » lu à l'heure de Paris : { key: 'AAAA-MM-JJ', weekday: 1 (lundi) … 7 (dimanche), seconds: secondes
// depuis minuit }. Changement d'heure compris (Intl).
function snkParisNow(now = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Paris',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value])
  );
  const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(parts.weekday) + 1;
  return {
    key: `${parts.year}-${parts.month}-${parts.day}`,
    weekday,
    seconds: Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
  };
}

// Pâques (algorithme de Meeus/Jones/Butcher), en millisecondes UTC à minuit
function snkEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return Date.UTC(year, month - 1, day);
}

// Jours fériés de France métropolitaine d'une année (« AAAA-MM-JJ ») : 8 fixes, plus lundi de Pâques, Ascension et
// lundi de Pentecôte. Pas d'expédition ces jours-là (client, 28 oct. 2026) ; les fermetures exceptionnelles s'ajoutent
// par le réglage « Jours sans expédition ».
function snkFrenchHolidays(year) {
  const key = (time) => new Date(time).toISOString().slice(0, 10);
  const easter = snkEaster(year);
  const fixed = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'].map((md) => `${year}-${md}`);
  const mobile = [1, 39, 50].map((offset) => key(easter + offset * 86400000));
  return [...fixed, ...mobile];
}

// Calendrier d'un élément (attributs de snippets/snk-ship-data) : fériés de l'année en cours et de la suivante plus
// les fermetures du réglage, et noms des jours (1 = lundi … 7 = dimanche)
function snkShipCalendar(dataset) {
  const year = Number(snkParisNow().key.slice(0, 4));
  const closures = (dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean);
  const weekdays = Object.fromEntries(
    (dataset.weekdays || '')
      .split(',')
      .filter(Boolean)
      .map((pair) => pair.split(':'))
      .map(([n, name]) => [Number(n), name])
  );
  return { holidays: new Set([...snkFrenchHolidays(year), ...snkFrenchHolidays(year + 1), ...closures]), weekdays };
}

// « Expédiée demain » / « Expédiée lundi » pour un état « prochain jour ouvré »
function snkNextDayText(state, dataset, weekdays) {
  const day = state.offset === 1 ? dataset.textTomorrow : weekdays[state.weekday] || dataset.textTomorrow;
  return (dataset.textNext || '').replace('__DAY__', day);
}

// État du bandeau : { mode: 'today' } (plus d'une heure avant la limite), { mode: 'count', seconds } (dernière
// heure), { mode: 'next', day, afterCutoff } (prochain jour ouvré). holidays : Set de « AAAA-MM-JJ ».
function snkCutoffState(paris, cutoff, holidays) {
  const [hour, minute] = cutoff.split(':').map(Number);
  const limit = hour * 3600 + (minute || 0) * 60;
  const working = (key, weekday) => weekday >= 1 && weekday <= 5 && !holidays.has(key);
  const todayWorking = working(paris.key, paris.weekday);
  const remaining = limit - paris.seconds;
  if (todayWorking && remaining > 0) return remaining > 3600 ? { mode: 'today', seconds: remaining } : { mode: 'count', seconds: remaining };
  const [y, m, d] = paris.key.split('-').map(Number);
  for (let i = 1; i <= 14; i++) {
    const date = new Date(Date.UTC(y, m - 1, d + i));
    const key = date.toISOString().slice(0, 10);
    const weekday = date.getUTCDay() || 7;
    if (working(key, weekday)) return { mode: 'next', offset: i, weekday, afterCutoff: todayWorking };
  }
  return { mode: 'next', offset: 1, weekday: (paris.weekday % 7) + 1, afterCutoff: todayWorking };
}

if (typeof customElements !== 'undefined' && !customElements.get('snk-cart-cutoff')) {
  customElements.define(
    'snk-cart-cutoff',
    class SnkCartCutoff extends HTMLElement {
      connectedCallback() {
        // Heure limite non confirmée par le client (réglage désactivé) : bandeau neutre rendu par Liquid, rien à faire
        if (this.hasAttribute('data-static')) return;
        const calendar = snkShipCalendar(this.dataset);
        this.holidays = calendar.holidays;
        this.weekdays = calendar.weekdays;
        // Pas « this.title » : propriété native d'un élément (attribut title)
        this.titleNode = this.querySelector('[data-snk-cut-title]');
        this.sub = this.querySelector('[data-snk-cut-sub]');
        this.icon = this.querySelector('[data-snk-cut-icon]');
        this.count = this.querySelector('[data-snk-cut-count]');
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      tick() {
        const state = snkCutoffState(snkParisNow(), this.dataset.cutoff || '13:00', this.holidays);
        const d = this.dataset;
        // Aujourd'hui (et dernière heure) : fond lime ; prochain jour ouvré : fond gris clair, trait lime
        const calm = state.mode === 'next';
        this.classList.toggle('snk-cutoff--calm', calm);
        this.classList.toggle('snk-cutoff--today', !calm);
        // Avec le texte du réglage, tout passe par la phrase (« Commande dans … ») : pas de case min : s séparée
        const sentence = Boolean(d.textCountdown) && state.mode !== 'next';
        this.icon.hidden = state.mode === 'count' && !sentence;
        this.count.hidden = state.mode !== 'count' || sentence;
        if (sentence) {
          // Compte à rebours toute la matinée (LG, 4 nov. 2026, remplace la wireframe) : « Commande dans 3 h 31 »,
          // minutes tronquées, donc le texte change une fois par minute ; dernière heure : « 14 min 50 s »
          const pad = (n) => String(n).padStart(2, '0');
          const time = state.mode === 'today'
            ? `${Math.floor(state.seconds / 3600)} h ${pad(Math.floor((state.seconds % 3600) / 60))}`
            : `${Math.floor(state.seconds / 60)} ${d.textMin} ${pad(state.seconds % 60)} ${d.textSec}`;
          this.titleNode.textContent = d.textCountdown.replace('[temps]', time);
          this.sub.textContent = d.textCountdownSub || '';
        } else if (state.mode === 'today') {
          this.titleNode.textContent = d.textToday;
          this.sub.textContent = d.textBefore;
        } else if (state.mode === 'count') {
          this.titleNode.textContent = d.textToday;
          this.sub.textContent = d.textWithin;
          const pad = (n) => String(n).padStart(2, '0');
          this.querySelector('[data-snk-cut-min]').textContent = pad(Math.floor(state.seconds / 60));
          this.querySelector('[data-snk-cut-sec]').textContent = pad(state.seconds % 60);
        } else {
          // Prochain jour ouvré : « demain » ou le nom du jour. Le minuteur continue de tourner (LG, 4 nov. 2026) :
          // un panier ouvert depuis la veille repasse de lui-même au compte à rebours le jour ouvré suivant
          this.titleNode.textContent = snkNextDayText(state, d, this.weekdays);
          this.sub.textContent = state.afterCutoff ? d.textAfter : d.textClosed;
        }
        this.hidden = false;
      }
    }
  );
}

// Ligne d'expédition sous le bouton d'achat (sections/snk-product-main.liquid) : même calcul que le bandeau, sans
// compte à rebours. Jour ouvré avant l'heure limite : « Commande avant 13 h : expédiée aujourd'hui · Reçue en … » ;
// sinon « Expédiée demain / lundi · Reçue en … ». En cas d'erreur, le texte rendu par Liquid reste.
if (typeof customElements !== 'undefined' && !customElements.get('snk-ship-line')) {
  customElements.define(
    'snk-ship-line',
    class SnkShipLine extends HTMLElement {
      connectedCallback() {
        try {
          const d = this.dataset;
          const { holidays, weekdays } = snkShipCalendar(d);
          const paris = snkParisNow();
          const state = snkCutoffState(paris, d.cutoff || '13:00', holidays);
          // Fiche produit (LG, 4 nov. 2026, modèle Spacegoods) : une phrase, dates de réception surlignées
          if (d.textDates && this.renderDates(d, paris, state, holidays)) return;
          const text = state.mode === 'next' ? snkNextDayText(state, d, weekdays) : d.textToday;
          if (!text) return;
          this.textContent = d.delay ? `${text} · ${d.delay}` : text;
        } catch (error) {
          // Texte de secours de Liquid conservé
        }
      }

      // « Reçue entre le jeu. 6 et le ven. 7 nov. » : jour d'expédition (aujourd'hui avant l'heure limite un jour ouvré,
      // sinon le jour ouvré suivant) + délai du pays du visiteur, lu dans son texte (« 48 à 72 h » → 2 à 3 jours, « 2 à 6
      // jours ouvrables »), compté en jours ouvrés du lundi au vendredi hors fériés : estimation prudente.
      renderDates(d, paris, state, holidays) {
        const numbers = (d.delay || '').match(/\d+/g);
        if (!numbers) return false;
        let [min, max = numbers[0]] = numbers.map(Number);
        if (/\d\s*h\b/i.test(d.delay) && min >= 24) {
          min = Math.ceil(min / 24);
          max = Math.ceil(max / 24);
        }
        const [y, m, day] = paris.key.split('-').map(Number);
        const working = (date) => {
          const weekday = date.getUTCDay() || 7;
          return weekday <= 5 && !holidays.has(date.toISOString().slice(0, 10));
        };
        const ship = new Date(Date.UTC(y, m - 1, day + (state.mode === 'next' ? state.offset : 0)));
        const after = (count) => {
          const date = new Date(ship);
          for (let n = 0; n < count; ) {
            date.setUTCDate(date.getUTCDate() + 1);
            if (working(date)) n++;
          }
          return date;
        };
        const format = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
        const mark = (date) => {
          const span = document.createElement('span');
          span.className = 'snk-ship-date';
          span.textContent = format.format(date);
          return span.outerHTML;
        };
        const first = after(min);
        const last = after(max);
        const template = first.getTime() === last.getTime() ? d.textDate : d.textDates;
        if (!template) return false;
        const escape = (text) => text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
        this.innerHTML = escape(template).replace('[min]', mark(first)).replace('[max]', mark(last));
        return true;
      }
    }
  );
}

if (typeof customElements !== 'undefined' && !customElements.get('snk-cart-tiers')) {
  customElements.define(
    'snk-cart-tiers',
    class SnkCartTiers extends HTMLElement {
      connectedCallback() {
        window.snkMoney?.apply(this);
        if (!this.hasAttribute('data-convert') || !window.snkMoney || this.dataset.mode === 'bf') return;
        // Devise du visiteur : total du panier ramené en euros au taux de Shopify, puis mêmes règles qu'en Liquid
        const rate = Number(window.Shopify?.currency?.rate) || 1;
        const threshold = Number(this.dataset.threshold);
        const totalEur = Number(this.dataset.total) / rate;
        const leftEur = Math.max(0, threshold - totalEur);
        const ship = leftEur === 0;
        const shipPct = Math.min(100, (totalEur / threshold) * 100);
        // Mode kit sans paire : le remplissage s'arrête avant le repère du cadeau (même règle qu'en Liquid)
        const kitPos = Number(this.dataset.kitPos) || 40;
        const pct = this.dataset.mode === 'kit' && Number(this.dataset.pairs) === 0 ? Math.min(shipPct, kitPos - 3) : shipPct;
        const left = this.querySelector('[data-snk-left]');
        if (left) left.textContent = window.snkMoney.format(window.snkMoney.convert(Math.round(leftEur), 'up'));
        this.querySelector('[data-snk-fill]').style.width = `${pct}%`;
        this.querySelector('[data-snk-ship-mark]')?.classList.toggle('is-ok', ship);
        this.querySelector('[data-snk-ship-label]')?.classList.toggle('is-ok', ship);
        // Même bascule pour la ligne de réassurance du bas du tiroir (snippets/cart-drawer.liquid, [data-snk-assure])
        const drawer = this.closest('cart-drawer') || document;
        drawer.querySelectorAll('[data-snk-when], [data-snk-assure] [data-snk-when]').forEach((node) => {
          node.hidden = node.dataset.snkWhen === 'free' ? !ship : ship;
        });
      }
    }
  );
}
